import { arrayOf, boolOf, fail, idOf, numberOf, objectOf, optionalText, textOf } from '../../../shared/api/wire'
import { decodeCurrentOperation } from '../../sale/api/contracts'
import type { PaymentState } from '../../sale/model/types'
import type { SlipRefundSale, SlipRefundSeat, SlipRefundSlip, SlipRefundSlips } from '../model/slipRefundTypes'

const PAYMENT_STATES: readonly PaymentState[] = ['NotRequired', 'Pending', 'InProgress', 'Succeeded', 'Failed', 'Unknown']

/** Копейки - целое: дробные «копейки» значат ответ другой схемы. */
function kopecksOf(value: unknown, field: string): number {
  const number = numberOf(value, field)
  return Number.isSafeInteger(number) && number >= 0 ? number : fail(`${field} - не целые копейки`)
}

function decodeSlip(raw: unknown, field: string): SlipRefundSlip {
  const item = objectOf(raw, field)
  return {
    id: idOf(item.id, `${field}.id`),
    amountKopecks: kopecksOf(item.amountKopecks, `${field}.amountKopecks`),
    processedAt: optionalText(item.processedAt, `${field}.processedAt`),
    returnedKopecks: kopecksOf(item.returnedKopecks, `${field}.returnedKopecks`),
    availableKopecks: kopecksOf(item.availableKopecks, `${field}.availableKopecks`),
    hasSale: boolOf(item.hasSale, `${field}.hasSale`),
  }
}

/** Слипы покупки кассы (`GET refunds/slip/slips`, RP-FB74): новые первыми, порядок службы сохраняется. */
export function decodeSlipRefundSlips(raw: unknown): SlipRefundSlips {
  const data = objectOf(raw, 'слипы кассы')
  return {
    deviceId: numberOf(data.deviceId, 'слипы кассы.deviceId'),
    lookbackDays: numberOf(data.lookbackDays, 'слипы кассы.lookbackDays'),
    items: arrayOf(data.items, 'слипы кассы.items').map((item, index) => decodeSlip(item, `слипы кассы.items[${index}]`)),
  }
}

/**
 * Возврат по слипу (`aqsi.sale` вида `SlipRefund`). Продажа и возврат по чеку тем же видом конверта здесь отвергаются: окно не покажет
 * чужую операцию как возврат по слипу. Ответ кабинета - в `currentOperation.providerMessage` (дословно) и в `lastMessage`.
 */
export function decodeSlipRefundSale(raw: unknown, field = 'возврат по слипу'): SlipRefundSale {
  const data = objectOf(raw, field)
  if (data.kind !== 'SlipRefund') fail(`${field}.kind: ожидался возврат по слипу`)
  const payment = textOf(data.payment, `${field}.payment`)
  return {
    id: idOf(data.id, `${field}.id`),
    totalKopecks: kopecksOf(data.totalKopecks, `${field}.totalKopecks`),
    payment: (PAYMENT_STATES as readonly string[]).includes(payment) ? (payment as PaymentState) : fail(`${field}.payment: неизвестное значение ${payment}`),
    isFinished: boolOf(data.isFinished, `${field}.isFinished`),
    slipId: optionalText(data.slipId, `${field}.slipId`),
    lastMessage: optionalText(data.lastMessage, `${field}.lastMessage`),
    version: numberOf(data.version, `${field}.version`),
    operation: decodeCurrentOperation(data.currentOperation, `${field}.currentOperation`),
  }
}

/**
 * `GET sales/active` глазами окна возврата по слипу: касса свободна, на ней открытый возврат по слипу или другая операция (продажа, возврат по
 * чеку) - её окно не ведёт.
 */
export function decodeSlipRefundSeat(raw: unknown): SlipRefundSeat {
  if (raw === null || raw === undefined) return { kind: 'none' }
  const data = objectOf(raw, 'операция кассы')
  return data.kind === 'SlipRefund' ? { kind: 'slip-refund', refund: decodeSlipRefundSale(raw, 'операция кассы') } : { kind: 'other' }
}
